import clsx from "clsx";
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import ResetTargeting from "../ResetTargeting";

interface StepperProps {
  selectedIndex: number;
  nextButtonComponent: JSX.Element;
  isMiningTargets?: boolean;
}

const STEPS = [
  { label: "Targeting", path: "targeting" },
  { label: "Audience", path: "audience" },
  { label: "Campaign Flow", path: "flows" },
  { label: "Schedule", path: "schedule" },
  { label: "Review", path: "review" },
];

export default function Stepper({
  selectedIndex,
  nextButtonComponent,
  isMiningTargets = false,
}: StepperProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isResetTargetingOpen, setIsResetTargetingOpen] = useState(false);

  const progress = `${(selectedIndex / (STEPS.length - 1)) * 100}%`;

  return (
    <>
      <div className="fixed lg:sticky inset-x-0 p-4 mt-auto lg:-mx-4 bottom-0 bg-white flex flex-col items-center justify-between gap-4 border-t border-t-black-200 lg:py-2.5 duration-500 lg:flex-row">
        <RenderIf condition={!isDesktop}>
          <div className="relative flex w-full flex-1">
            {/* Progress */}
            <div className="absolute top-0.5 h-1 w-full overflow-hidden rounded-full bg-black-200">
              <div
                className="h-full rotate-180 bg-purple-400"
                style={{ width: progress }}
              />
            </div>

            {/* Steps */}
            <div className="relative flex w-full justify-between">
              {STEPS.map(({ path }, index) => (
                <div
                  className={clsx(
                    "relative z-10 flex size-2 rounded-full",
                    index > selectedIndex && "bg-black-200",
                    index === selectedIndex && "bg-purple-400",
                  )}
                  key={path}
                />
              ))}
            </div>
          </div>
        </RenderIf>

        <div className="mx-auto flex w-full items-center justify-between lg:max-w-[1920px]">
          <Button
            variant="secondary-black"
            className={selectedIndex <= 1 && "invisible"}
            asChild
          >
            <Link
              to={`../${STEPS[selectedIndex >= 1 ? selectedIndex - 1 : 0].path}`}
            >
              Back
            </Link>
          </Button>

          {isDesktop ? (
            <div className="flex items-center">
              {STEPS.map(({ label, path }, index) => {
                if (selectedIndex === index) {
                  return (
                    <div
                      key={path}
                      className={clsx(
                        "flex h-8 items-center text-nowrap rounded-full border-x border-x-transparent px-4 text-button-xs capitalize",
                        getWhitelabelCustomization()?.primary_color
                          ? "bg-whitelabel-primary text-whitelabel-contrast"
                          : "bg-purple-500 text-white",
                      )}
                    >
                      {label}
                    </div>
                  );
                }
                if (selectedIndex > index) {
                  const isTargetingStep = index === 0;
                  return (
                    <Fragment key={path}>
                      <Button
                        key={path}
                        variant="outline-black"
                        size="sm"
                        className="capitalize"
                        asChild={!isTargetingStep}
                        // special logic for editing targeting
                        onClick={
                          isTargetingStep
                            ? () => setIsResetTargetingOpen(true)
                            : undefined
                        }
                        // editing targeting is disabled while mining targets
                        disabled={isMiningTargets}
                      >
                        {isTargetingStep ? (
                          label
                        ) : (
                          <Link to={`../${path}`}>{label}</Link>
                        )}
                      </Button>
                      <div className="h-px w-1 bg-purple-100 xl:w-6" />
                    </Fragment>
                  );
                }
                return (
                  <span
                    key={path}
                    className="ml-1 text-nowrap border-x border-x-transparent px-4 text-button-xs capitalize text-black-400 xl:ml-6"
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="flex-1 text-center text-button-sm capitalize">
              {STEPS[selectedIndex].label}
            </span>
          )}

          {nextButtonComponent}
        </div>
      </div>
      <ResetTargeting
        isOpen={isResetTargetingOpen}
        setIsOpen={setIsResetTargetingOpen}
      />
    </>
  );
}
